import { ColDef } from "ag-grid-community";
import {
  RowData,
  JsonSchema,
  JsonSchemaItems,
  JsonSchemaItemProperty,
  JsonSchemaProperty,
  PrimitiveValueType,
  GenericRowData,
  LandingPageOptions,
  SectionValue,
  RowDataValueType,
} from "../../../types";
import {
  DOLLAR_PREFIX_FOR_FIELDS,
  NEW_ENTRY_RESTRICTED_SETUPS,
  PRIVLIB_SETUPS,
  RESTRICTED_SETUPS,
  ROW_DATA_KEYS,
  SCHEMA_TITLES,
} from "../constants";
import {
  determineSetupMode,
  getInputType,
  getSchemaSections,
  getTitleFromKey,
} from "./schemaHelpers";
import { getSelectedOptions } from "./optionHelpers";

export const filterRecords = (
  term: string,
  records: RowData[],
  gridFields: Set<string>,
  getValue: (fieldName: string, row: RowData) => unknown = (fieldName, row) =>
    row[fieldName],
): RowData[] => {
  if (!term) return records;

  return records.filter((row) =>
    Object.keys(row)
      .filter((key) => gridFields.has(key))
      .some((key) => matchesSearch(getValue(key, row), term)),
  );
};

export const matchesSearch = (value: unknown, search: string): boolean => {
  if (value == null) return false;

  const regex = new RegExp(search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");

  if (typeof value === "string") return regex.test(value);
  if (typeof value === "number" || typeof value === "boolean")
    return regex.test(String(value));

  if (Array.isArray(value)) return value.some((v) => matchesSearch(v, search));

  if (typeof value === "object")
    return Object.values(value).some((v) => matchesSearch(v, search));

  return false;
};

export const formatFieldValueToString = (
  fieldName: string,
  fieldValue: RowDataValueType,
): string => {
  if (fieldValue == null || fieldValue === "") return "-";

  const isDollarField = DOLLAR_PREFIX_FOR_FIELDS.some((term) =>
    fieldName.toLowerCase().includes(term.toLowerCase()),
  );

  const formatValue = (value: unknown): string => {
    if (value == null) return "";

    if (Array.isArray(value)) {
      const selectedOptions = getSelectedOptions(fieldValue);
      const values = selectedOptions.map((option) => option.value).join(", ");
      return values;
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (typeof value === "number") {
      return value.toString();
    }

    if (typeof value === "string") {
      return value;
    }

    if (typeof value === "object") {
      return Object.values(value).map(formatValue).join(", ");
    }

    return "";
  };

  const formatted = formatValue(fieldValue);

  return isDollarField ? `$${formatted}` : formatted;
};

export const isSetupRecordActionsRestricted = (
  setup: string,
  selectedDashboardTabSectionKey: string | undefined,
): boolean => {
  if (selectedDashboardTabSectionKey) {
    return (
      PRIVLIB_SETUPS.includes(selectedDashboardTabSectionKey) ||
      RESTRICTED_SETUPS.includes(selectedDashboardTabSectionKey)
    );
  }
  return (
    PRIVLIB_SETUPS.includes(setup) ||
    RESTRICTED_SETUPS.includes(setup) ||
    NEW_ENTRY_RESTRICTED_SETUPS.includes(setup)
  );
};

export const getNewRow = (schema: JsonSchema): RowData => {
  const newRow: RowData = {};
  const setupMode = determineSetupMode(schema);
  const FLAT_SECTIONS = getSchemaSections(schema, undefined).map(
    (section) => section.sectionKey,
  );

  Object.entries(schema.sections).forEach(([tabKey, sectionValue]) => {
    if (tabKey === "grid_fields") return;

    const fields = collectFields(sectionValue);
    const tabRow: RowData = {};

    fields.forEach((key) => {
      const property = schema.properties[key];

      if (getInputType(property.type) === "array" && property.items) {
        const items = property.items as JsonSchemaItems;

        if (items.properties) {
          const rowItem = Object.fromEntries(
            Object.keys(items.properties).map((propKey) => [
              propKey,
              getDefaultInputValue(
                items?.properties?.[propKey] ?? ({} as JsonSchemaItemProperty),
              ),
            ]),
          );

          rowItem[ROW_DATA_KEYS.ACTIVE_STATUS] = "active";

          tabRow[key] = [rowItem];
        } else {
          tabRow[key] = [];
        }
      } else if (property.properties) {
        const nestedObject = Object.keys(property.properties).reduce(
          (acc, propKey) => {
            acc[propKey] = getDefaultInputValue(property.properties![propKey]);
            return acc;
          },
          {} as RowData,
        );

        tabRow[key] = nestedObject;
      } else {
        tabRow[key] = getDefaultInputValue(property);
      }
    });

    // NORMAL_TAB_SETUP: tabs are not merged at schema level but the
    // resulting RowData is always a single flat object — merge all tabs in.
    if (
      setupMode === "NORMAL_TAB_SETUP" ||
      setupMode === "INVENTORY_BUILDINGS_SETUP" ||
      FLAT_SECTIONS.includes(tabKey)
    ) {
      Object.assign(newRow, tabRow);
    } else {
      // Nested under the tab section key
      newRow[tabKey] = tabRow;
    }
  });

  return newRow;
};

export const collectFields = (section: SectionValue): string[] => {
  if (Array.isArray(section)) {
    return section;
  } else if (typeof section === "object" && section !== null) {
    return Object.values(section).flatMap(collectFields);
  }
  return [];
};

const getDefaultInputValue = (
  property: JsonSchemaProperty | JsonSchemaItemProperty,
): PrimitiveValueType => {
  if (property.default !== undefined) {
    return property.default;
  }
  return "";
};

export const getColDefs = (schema: JsonSchema): ColDef[] => {
  const columnDefs: ColDef[] = [];
  const addedFields = new Set<string>();
  const allFields = Object.values(schema.sections).flatMap(collectFields);
  const columnOverrides = SETUP_COLUMN_CONFIG[schema.title] ?? {};

  allFields.forEach((fieldKey) => {
    const property = schema.properties[fieldKey];
    if (property && !addedFields.has(fieldKey)) {
      columnDefs.push({
        field: fieldKey,
        headerName: property.title,
        ...columnOverrides[fieldKey],
      });
      addedFields.add(fieldKey);
    }
  });

  return columnDefs;
};

export const getHeaderLabel = (
  selected: LandingPageOptions,
  selectedDashboardTabSectionKey?: string,
): string => {
  const { store, department, setup } = selected;

  const parts: string[] = [store.label];

  if (department.label) {
    parts.push(department.label);

    const setupLabel = selectedDashboardTabSectionKey
      ? getTitleFromKey(selectedDashboardTabSectionKey)
      : setup.label;

    if (setupLabel) {
      parts.push(setupLabel);
    }
  }

  return parts.join(" | ");
};

export const isCustomRowActionDisabled = (
  rowData: RowData,
  rowDataList: RowData[],
  setupName: string,
): boolean => {
  if (
    setupName === SCHEMA_TITLES.GL_FISCAL_YEAR_CALENDAR &&
    rowDataList.length > 0
  ) {
    return rowDataList[0].id !== rowData.id;
  }

  return false;
};

export const getPage = (
  index: number,
  pageSize: number,
  tableData: RowData[],
): GenericRowData => {
  const workingDatabase = structuredClone(tableData);

  let firstPage = false;
  let lastPage = false;
  const error = false;

  let start = pageSize * (index - 1);
  if (start < 0) {
    start = 0;
    firstPage = true;
  }
  if (start > workingDatabase.length)
    return {
      pageCount: undefined,
      totalCount: workingDatabase.length,
      data: undefined,
      error: true,
      lastPage: false,
      firstPage: false,
    };

  let end = pageSize * index;
  if (end > workingDatabase.length) {
    end = workingDatabase.length;
    lastPage = true;
  }
  const page = workingDatabase.slice(start, end);

  return {
    pageCount: end - start,
    totalCount: workingDatabase.length,
    data: page,
    error,
    firstPage,
    lastPage,
  };
};

export const getPageSummaryText = (
  currentPage: number,
  totalPages: number,
): string => {
  return `Page ${currentPage} of ${totalPages}`;
};

export const getRowsSummaryText = (
  currentPage: number,
  pageSize: number,
  totalRows: number,
): string => {
  if (totalRows === 0) {
    return `${totalRows}-${totalRows} of ${totalRows}`;
  }
  const rowsFrom = (currentPage - 1) * pageSize + 1;
  const rowsTo = currentPage * pageSize;
  return `${rowsFrom}-${
    rowsTo > totalRows ? totalRows : rowsTo
  } of ${totalRows}`;
};

const normalizeSortableValue = (value?: RowDataValueType): string => {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  return "";
};

const MONTH_INDEX_BY_NAME: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

export const monthFirstSort = (a?: string, b?: string): number => {
  const valueA = normalizeSortableValue(a);
  const valueB = normalizeSortableValue(b);

  if (valueA === "" && valueB === "") return 0;
  if (valueA === "") return 1;
  if (valueB === "") return -1;

  const monthA = MONTH_INDEX_BY_NAME[valueA.toLowerCase()];
  const monthB = MONTH_INDEX_BY_NAME[valueB.toLowerCase()];

  if (monthA !== undefined && monthB !== undefined) {
    return monthA - monthB;
  }

  return numberFirstSort(a, b);
};

export const numberFirstSort = (a?: string, b?: string): number => {
  const valueA = normalizeSortableValue(a);
  const valueB = normalizeSortableValue(b);

  if (valueA === "" && valueB === "") return 0;
  if (valueA === "") return 1;
  if (valueB === "") return -1;

  const numA = Number(valueA);
  const numB = Number(valueB);

  const isANumber = !Number.isNaN(numA);
  const isBNumber = !Number.isNaN(numB);

  if (isANumber && isBNumber) {
    return numA - numB;
  }

  if (isANumber) return -1;
  if (isBNumber) return 1;

  return valueA.localeCompare(valueB, undefined, { numeric: true });
};

export const SETUP_COLUMN_CONFIG: Record<
  string,
  Record<string, Partial<ColDef>>
> = {
  CashClearingSetup: {
    integrating_application: {
      width: 110,
      minWidth: 110,
      maxWidth: 110,
      filter: false,
    },
    transaction_type: {
      width: 100,
      minWidth: 130,
      maxWidth: 130,
      filter: false,
    },
    journal_num: {
      width: 130,
      minWidth: 130,
      filter: false,
    },
    cash_clearing_company_group_id: {
      width: 175,
      minWidth: 175,
      filter: false,
    },
    cash_clearing_account: {
      width: 160,
      minWidth: 160,
      filter: false,
    },
    cash_in_bank_company_group_id: {
      width: 170,
      minWidth: 170,
      filter: false,
    },
    cash_in_bank_account: {
      width: 160,
      minWidth: 160,
      filter: false,
    },
    surcharge_account: {
      width: 130,
      minWidth: 130,
      filter: false,
    },
    is_enabled: {
      width: 80,
      minWidth: 100,
      maxWidth: 100,
      filter: false,
    },
  },

  GLCalendar: {
    posting_year: {
      sort: "desc",
      sortIndex: 0,
    },
    posting_month: {
      sort: "asc",
      sortIndex: 1,
      comparator: monthFirstSort,
    },
  },
};
